import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  clearAllTokens,
  setAccessToken,
  setExpiresAt,
  setRefreshToken,
} from '@src/modules/security/tokenStorage';

export interface AuthUserState {
  id: number;
  userId: string;
  email: string;
  name: string;
  role: 'user' | 'admin';
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
}

interface AuthState {
  user: AuthUserState | null;
  // セキュリティ上の理由により、トークンは状態に保存せずセキュアストレージを使用
  isAuthenticated: boolean;
}

// localStorageにユーザー情報を保存
function saveUserToStorage(user: AuthUserState | null): void {
  try {
    if (user) {
      localStorage.setItem('auth_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('auth_user');
    }
  } catch (error) {
    console.error('Failed to save user to localStorage:', error);
  }
}

// 初期状態：起動時はbootstrap.tsで復元されるため、空の状態で開始
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuth(
      state,
      action: PayloadAction<{
        user: AuthUserState;
        accessToken: string;
        refreshToken: string;
        expiresAt: string;
      }>
    ) {
      state.user = action.payload.user;
      state.isAuthenticated = true;

      // トークンはセキュアストレージに保存
      setAccessToken(action.payload.accessToken);
      setRefreshToken(action.payload.refreshToken);
      setExpiresAt(action.payload.expiresAt);

      // ユーザー情報をlocalStorageに保存
      saveUserToStorage(action.payload.user);
    },
    clearAuth(state) {
      state.user = null;
      state.isAuthenticated = false;

      // セキュアストレージからトークンを削除
      clearAllTokens();

      // localStorageからユーザー情報を削除
      saveUserToStorage(null);
    },
    // 認証状態のみを更新（トークンは既にストレージにある場合）
    setAuthenticationStatus(state, action: PayloadAction<boolean>) {
      state.isAuthenticated = action.payload;
    },
  },
});

export const { setAuth, clearAuth } = authSlice.actions;

// Selectors
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
const _selectAuthUser = (state: { auth: AuthState }) => state.auth.user;

export default authSlice.reducer;
